import { checkFail, type PresentCheck } from './core/core.ts';
import { isNumber, isString } from './identity.ts';

const trimmableRe = /^\s|\s$/;

// Strings must consist entirely of a valid number. NaN is rejected.
export function asNumber(s: unknown): number | undefined;
export function asNumber(s: unknown, check: typeof PresentCheck): number;
export function asNumber(s: unknown, check?: typeof PresentCheck): number | undefined {
  if (isNumber(s)) {
    if (!isNaN(s)) {
      return s;
    }
    // https://stackoverflow.com/a/35759874/8107589
  } else if (isString(s) && !trimmableRe.test(s) && !isNaN(Number(s))) {
    const result = parseFloat(s);
    if (!isNaN(result)) {
      return result;
    }
  }
  if (check) {
    checkFail(
      'asNumber failed.',
      isString(s) && trimmableRe.test(s)
        ? 'Input contains whitespace.'
        : !isNumber(s) && !isString(s)
          ? 'Input is not a number or string.'
          : 'Input is NaN.',
    );
  }
  return undefined;
}

// https://stackoverflow.com/questions/11665884/how-can-i-parse-a-string-with-a-comma-thousand-separator-to-a-number
export function parseHumanNumber(s: string): number | undefined {
  // TODO: This should be modified to support i18n, but that is difficult for this case.
  return asNumber(s.replace(/,/g, ''));
}

export function numericCompare<T>(a: T, b: T): number {
  if (a < b) {
    return -1;
  }
  if (b < a) {
    return 1;
  }
  return 0;
}

export function tupleCompare<T>(elCompare: (a: T, b: T) => number) {
  return <A extends T[]>(a: A, b: A) => {
    for (let i = 0; i < a.length; i += 1) {
      const result = elCompare(a[i], b[i]);
      if (result !== 0) {
        return result;
      }
    }
    return 0;
  };
}

export function derivedCompare<T, U>(fn: (el: T) => U, compare: (a: U, b: U) => number) {
  return (a: T, b: T) => compare(fn(a), fn(b));
}

// Returns the unique *positive* result for value % modulus.
// https://stackoverflow.com/a/17323608
export function mod(value: number, modulus: number): number {
  return ((value % modulus) + modulus) % modulus;
}

// Returns the integer component of `value / divisor`.
// value - div(value, divisor) * divisor === mod(value, divisor)
export function div(value: number, divisor: number): number {
  return Math.floor(value / divisor);
}

export function scaledRound(v: number, scale: number) {
  return Math.round(v * scale) / scale;
}

export function scaledFloor(v: number, scale: number) {
  return Math.floor(v * scale) / scale;
}

export function scaledCeiling(v: number, scale: number) {
  return Math.ceil(v * scale) / scale;
}

export function hypotenuse(sideA: number, sideB: number): number {
  return Math.sqrt(sideA * sideA + sideB * sideB);
}

export function inInclusiveRange(v: number, min: number, max: number) {
  return v >= min && v <= max;
}

export function clamp(v: number, a: number, b: number) {
  if (b < a) {
    return v < b ? b : v > a ? a : v;
  } else {
    return v < a ? a : v > b ? b : v;
  }
}

type Enumerate<N extends number, Acc extends number[] = []> = Acc['length'] extends N
  ? Acc[number]
  : Enumerate<N, [...Acc, Acc['length']]>;

export type Index<T extends number> = number extends T ? number : Enumerate<T>;
export type Range<T extends number, S extends number> = Exclude<Enumerate<T>, Enumerate<S>>;

export function range<T extends number>(size: T): Array<Index<T>>;
export function range<T extends number, S extends number>(
  size: T,
  startWith: S,
): Array<Range<T, S>>;
export function range<T extends number, S extends number>(
  size: T,
  startWith?: S,
): Array<Range<T, S>> {
  return new Array<number>(size).fill(0).map((_, i) => i + (startWith ?? 0)) as Array<Range<T, S>>;
}

// TODO: Accept number or Measure here.
export function lerp(ratio: number, start: number, end: number, doClamp = true) {
  if (doClamp) {
    ratio = clamp(ratio, 0, 1);
  }
  return start + (end - start) * ratio;
}

export function invLerp(interpolatedValue: number, start: number, end: number, doClamp = true) {
  let ratio = (interpolatedValue - start) / (end - start);
  if (doClamp) {
    ratio = clamp(ratio, 0, 1);
  }
  return ratio;
}

// |a-b| < ε
export function equalsWithinEpsilon(a: number, b: number, epsilon: number) {
  return Math.abs(b - a) < epsilon;
}

// A function whose domain is the percentage of time elapsed.
// CSS curves (and visual cheat sheet): https://easings.net/
export type CurveFunction = (timeRatio: number) => number;

// Easings: https://easings.net/

export function easeInQuad(x: number): number {
  return x * x;
}

export function easeInExpo(x: number): number {
  return x === 0 ? 0 : Math.pow(2, 10 * x - 10);
}

export function easeOutQuad(x: number): number {
  return 1 - (1 - x) * (1 - x);
}

export function easeOutExpo(x: number): number {
  return x === 1 ? 1 : 1 - Math.pow(2, -10 * x);
}
